<script setup>
import ImportApikomContentAction from '@frontJs/components/actions/projectManagement/seo/ImportApikomContentAction.vue';
import ToggleApikomContentPendedAtAction from '@frontJs/components/actions/projectManagement/seo/ToggleApikomContentPendedAtAction.vue';
import ToggleApikomContentProcessedAtAction from '@frontJs/components/actions/projectManagement/seo/ToggleApikomContentProcessedAtAction.vue';
import UpdateTenantECommerceCategoryContentAction from '@frontJs/components/actions/projectManagement/seo/UpdateTenantECommerceCategoryContentAction.vue';
import UpdateTenantECommerceProductContentAction from '@frontJs/components/actions/projectManagement/seo/UpdateTenantECommerceProductContentAction.vue';
import CheckIcon from '@iconify-vue/ph/check';
import ClockIcon from '@iconify-vue/ph/clock';
import DocumentTextIcon from '@iconify-vue/ph/file-text';
import NotePencilIcon from '@iconify-vue/ph/note-pencil';
import PencilSimpleIcon from '@iconify-vue/ph/pencil-simple';
import PlusIcon from '@iconify-vue/ph/plus';
import UploadSimpleIcon from '@iconify-vue/ph/upload-simple';
import XMarkIcon from '@iconify-vue/ph/x';
import DisplayContentAction from '@js/components/actions/DisplayContentAction.vue';
import ActionButton from '@js/components/buttons/ActionButton.vue';
import ExportButton from '@js/components/buttons/ExportButton.vue';
import DataTableLayout from '@js/components/tables/DataTableLayout.vue';
import BaseCell from '@js/components/tables/cells/BaseCell.vue';
import LinkCell from '@js/components/tables/cells/LinkCell.vue';
import NumberCell from '@js/components/tables/cells/NumberCell.vue';
import PillCell from '@js/components/tables/cells/PillCell.vue';
import StatusCell from '@js/components/tables/cells/StatusCell.vue';
import TextCell from '@js/components/tables/cells/TextCell.vue';
import { useDataTable } from '@js/utils/dataTable';
import { formatDate } from '@js/utils/formatter';
import { useRoute } from '@js/utils/route';
import { clearToasts, toastCreated, toastUpdated } from '@js/utils/toast';
import { createColumnHelper } from '@tanstack/vue-table';
import { h } from 'vue';

const { tenant, websiteVariants, ongoing, pending, done, standard, premium } =
    defineProps({
        tenant: {
            type: Object,
            required: true,
        },
        websiteVariants: {
            type: Array,
            required: true,
        },
        ongoing: {
            type: Boolean,
            required: false,
        },
        pending: {
            type: Boolean,
            required: false,
        },
        done: {
            type: Boolean,
            required: false,
        },
        standard: {
            type: Boolean,
            required: false,
        },
        premium: {
            type: Boolean,
            required: false,
        },
    });

const { route } = useRoute();

const columnHelper = createColumnHelper();

function updateActionCallback() {
    clearToasts();
    toastUpdated();
    updateData();
}

const {
    loading,
    table,
    count,
    globalFilter,
    setGlobalFilter,
    availableFilters,
    filters,
    setFilters,
    selectionIds,
    updateData,
    exportData,
} = useDataTable({
    columns: [
        columnHelper.accessor(() => null, {
            id: 'id',
            header: () => 'ID',
            cell: (props) =>
                h(NumberCell, {
                    number: props.row.original.id,
                }),
        }),
        columnHelper.accessor(() => null, {
            id: 'websiteVariant',
            header: () => 'Site',
            cell: (props) =>
                h(PillCell, {
                    pillValue: props.row.original.websiteVariant.name,
                }),
        }),
        columnHelper.accessor(() => null, {
            id: 'groupOrder',
            header: () => 'Groupe',
            cell: (props) =>
                h(PillCell, {
                    pillValue: props.row.original.group.label,
                }),
        }),
        columnHelper.accessor(() => null, {
            id: 'url',
            header: () => 'URL',
            cell: (props) =>
                h(LinkCell, {
                    text: props.row.original.url,
                    href: props.row.original.url,
                }),
        }),
        columnHelper.accessor(() => null, {
            id: 'type',
            header: () => 'Type',
            cell: (props) =>
                h(PillCell, {
                    pillValue: props.row.original.type.label,
                }),
        }),
        columnHelper.accessor(() => null, {
            id: 'updatesDescription',
            header: () => 'MAJ description',
            cell: (props) =>
                h(StatusCell, {
                    status: props.row.original.updatesDescription,
                }),
            enableSorting: false,
        }),
        columnHelper.accessor(() => null, {
            id: 'updatesMeta',
            header: () => 'MAJ meta',
            cell: (props) =>
                h(StatusCell, {
                    status: props.row.original.updatesMeta,
                }),
            enableSorting: false,
        }),
        columnHelper.accessor(() => null, {
            id: 'date',
            header: () => 'Date',
            cell: (props) =>
                props.row.original.date
                    ? h(TextCell, {
                          text: formatDate(
                              props.row.original.date,
                              false,
                              false,
                              false,
                              true,
                              true,
                          ),
                      })
                    : null,
        }),
        columnHelper.accessor(() => null, {
            id: 'pendedAt',
            header: () => 'En attente le',
            cell: (props) =>
                props.row.original.pendedAt !== null
                    ? h(TextCell, {
                          text: formatDate(props.row.original.pendedAt, false),
                      })
                    : null,
        }),
        columnHelper.display({
            id: 'details',
            header: 'Détails',
            width: 0,
            cell: (props) =>
                h(
                    BaseCell,
                    {},
                    {
                        default: () =>
                            props.row.original.comment
                                ? h(
                                      DisplayContentAction,
                                      {
                                          title: 'Commentaire',
                                          content: props.row.original.comment,
                                      },
                                      {
                                          trigger: () =>
                                              h(ActionButton, {
                                                  size: 'small',
                                                  title: 'Voir le commentaire',
                                                  type: 'primary',
                                                  square: true,
                                                  icon: DocumentTextIcon,
                                              }),
                                      },
                                  )
                                : null,
                    },
                ),
        }),
        columnHelper.display({
            id: 'actions',
            header: 'Actions',
            width: 0,
            cell: (props) =>
                h(
                    BaseCell,
                    {},
                    {
                        default: () => [
                            props.row.original.processedAt === null
                                ? h(
                                      ToggleApikomContentPendedAtAction,
                                      {
                                          tenant,
                                          seoApikomContent: props.row.original,
                                          callback: updateActionCallback,
                                      },
                                      {
                                          button: ({ action }) =>
                                              h(ActionButton, {
                                                  size: 'small',
                                                  type: 'secondary',
                                                  square: true,
                                                  callback: () => action(),
                                                  title: props.row.original
                                                      .pendedAt
                                                      ? 'Détraiter'
                                                      : 'Mettre en attente',
                                                  icon: props.row.original
                                                      .pendedAt
                                                      ? XMarkIcon
                                                      : ClockIcon,
                                              }),
                                      },
                                  )
                                : null,
                            h(
                                ToggleApikomContentProcessedAtAction,
                                {
                                    tenant,
                                    seoApikomContent: props.row.original,
                                    callback: updateActionCallback,
                                },
                                {
                                    button: ({ action }) =>
                                        h(ActionButton, {
                                            size: 'small',
                                            type: 'secondary',
                                            square: true,
                                            callback: () => action(),
                                            title: props.row.original
                                                .processedAt
                                                ? 'Détraiter'
                                                : 'Traiter',
                                            icon: props.row.original.processedAt
                                                ? XMarkIcon
                                                : CheckIcon,
                                        }),
                                },
                            ),
                            h(ActionButton, {
                                size: 'small',
                                square: true,
                                title: 'Modifier',
                                icon: PencilSimpleIcon,
                                href: route(
                                    'front.projectManagement.seo.updateApikomContent',
                                    {
                                        tenant: tenant.id,
                                        id: props.row.original.id,
                                    },
                                ),
                            }),
                            props.row.original.eCommerceCategory
                                ? h(
                                      UpdateTenantECommerceCategoryContentAction,
                                      {
                                          tenant,
                                          eCommerceCategory:
                                              props.row.original
                                                  .eCommerceCategory,
                                          seoApikomContent: props.row.original,
                                          callback: updateData,
                                      },
                                      {
                                          trigger: () =>
                                              h(ActionButton, {
                                                  size: 'small',
                                                  square: true,
                                                  icon: NotePencilIcon,
                                                  title: 'Modifier le contenu',
                                                  type: 'primary',
                                              }),
                                      },
                                  )
                                : null,
                            props.row.original.eCommerceProduct
                                ? h(
                                      UpdateTenantECommerceProductContentAction,
                                      {
                                          tenant,
                                          eCommerceProduct:
                                              props.row.original
                                                  .eCommerceProduct,
                                          seoApikomContent: props.row.original,
                                          callback: updateData,
                                      },
                                      {
                                          trigger: () =>
                                              h(ActionButton, {
                                                  size: 'small',
                                                  square: true,
                                                  icon: NotePencilIcon,
                                                  title: 'Modifier le contenu',
                                                  type: 'primary',
                                              }),
                                      },
                                  )
                                : null,
                        ],
                    },
                ),
        }),
    ],
    url: route('front.projectManagement.seo.postApikomContents', {
        tenant: tenant.id,
    }),
    exportUrl: route('front.projectManagement.seo.postExportApikomContents', {
        tenant: tenant.id,
    }),
    data: {
        ongoing: ongoing,
        pending: pending,
        done: done,
        standard: standard,
        premium: premium,
    },
    selectionIdKey: 'id',
    columnPinning: {
        right: ['details', 'actions'],
    },
    columnVisibility: {
        pendedAt: pending === true,
        type: !(standard || premium),
    },
});

function importCallback() {
    toastCreated();
    updateData();
}
</script>

<template>
    <DataTableLayout
        :table="table"
        :count="count"
        :global-filter="globalFilter"
        :set-global-filter="setGlobalFilter"
        :available-filters="availableFilters"
        :filters="filters"
        :set-filters="setFilters"
        :loading="loading"
    >
        <template #actions>
            <ExportButton
                :csv-export-callback="() => exportData('csv')"
                :xlsx-export-callback="() => exportData('xlsx')"
                :disabled="selectionIds.length === 0"
            />

            <ImportApikomContentAction
                :tenant="tenant"
                :website-variants="websiteVariants"
                :callback="importCallback"
            >
                <template #trigger>
                    <ActionButton
                        size="small"
                        :left-icon="UploadSimpleIcon"
                        type="primary"
                    >
                        Importer
                    </ActionButton>
                </template>
            </ImportApikomContentAction>

            <ActionButton
                size="small"
                :href="
                    route('front.projectManagement.seo.createApikomContent', {
                        tenant: tenant.id,
                    })
                "
                :left-icon="PlusIcon"
            >
                Ajouter
            </ActionButton>
        </template>
    </DataTableLayout>
</template>
